import React, { useState, useEffect } from 'react';
import { Databases, Query } from 'appwrite';
import appwriteClient from '../appwrite.config';
import { ToastContainer, toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import * as FileSaver from 'file-saver';
import XLSX from 'xlsx';
import { Search, Download, Activity, TrendingUp, Calendar, MapPin, Package } from 'lucide-react';

const TyreLifecycleAnalysis = () => {
  const [tyreSerialNumber, setTyreSerialNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [tyreData, setTyreData] = useState(null);
  
  const databases = new Databases(appwriteClient);

  // Normalize date format to yyyy/mm/dd
  const normalizeDateFormat = (dateStr) => {
    if (!dateStr) return null;
    
    const parts = dateStr.split('/');
    if (parts.length !== 3) return dateStr;
    
    if (parts[0].length <= 2 && parts[2].length === 4) {
      return `${parts[2]}/${parts[0].padStart(2, '0')}/${parts[1].padStart(2, '0')}`;
    }
    
    return dateStr;
  };

  // Convert date string to comparable format
  const dateToComparable = (dateStr) => {
    if (!dateStr) return 0;
    const normalized = normalizeDateFormat(dateStr);
    const parts = normalized.split('/');
    if (parts.length === 3) {
      return new Date(parts[0], parts[1] - 1, parts[2]).getTime();
    }
    return 0;
  };

  // Process fitment data for single tyre
  const processSingleTyre = (rawData) => {
    if (rawData.length === 0) return null;

    const normalizedData = rawData.map(row => ({
      ...row,
      fitmentDate: normalizeDateFormat(row.fitmentDate),
      removalDate: normalizeDateFormat(row.removalDate)
    })).sort((a, b) => dateToComparable(b.fitmentDate) - dateToComparable(a.fitmentDate));

    const latestRecord = normalizedData[0];
    const latestStatus = latestRecord.tyreStatus;
    
    let relevantRecords = [];
    
    if (latestStatus === "New Running Tyre") {
      relevantRecords = normalizedData.filter(r => 
        r.tyreStatus === "New Tyre" || r.tyreStatus === "New Running Tyre"
      );
    } else if (latestStatus === "Retread Running Tyre") {
      relevantRecords = normalizedData.filter(r => 
        r.tyreStatus === "Retread Tyre" || r.tyreStatus === "Retread Running Tyre"
      );
    } else if (latestStatus === "New Tyre" || latestStatus === "Retread Tyre") {
      relevantRecords = [latestRecord];
    }
    
    const recordsWithRuns = relevantRecords.map(record => {
      const fitmentODO = parseFloat(record.ftmentODO) || 0;
      const removalODO = parseFloat(record.removalODO) || 0;
      const fitmentTD = parseFloat(record.TD) || 0;
      const removalTD = parseFloat(record.removalTD) || 0;
      
      const hasRemovalODO = record.removalODO && removalODO > 0;
      
      return {
        ...record,
        run: hasRemovalODO ? removalODO - fitmentODO : null,
        tdDiff: hasRemovalODO ? removalTD - fitmentTD : null,
        hasRemovalODO: hasRemovalODO
      };
    });
    
    const totalRun = recordsWithRuns
      .filter(record => record.hasRemovalODO)
      .reduce((sum, record) => sum + record.run, 0);
    
    const totalTDDiff = recordsWithRuns
      .filter(record => record.hasRemovalODO)
      .reduce((sum, record) => sum + record.tdDiff, 0);
    
    return {
      tyreSerialNumber: latestRecord.tyreSerialNumber,
      latestStatus: latestStatus,
      totalRun: totalRun,
      totalTDDiff: totalTDDiff,
      recordCount: recordsWithRuns.length,
      completedFitments: recordsWithRuns.filter(r => r.hasRemovalODO).length,
      records: recordsWithRuns,
      fleetName: latestRecord.fleetName,
      vehicleType: latestRecord.vehicleType,
      tyreSize: latestRecord.tyreSize,
      tyreMakeandPattern: latestRecord.tyreMakeandPattern,
      treadMakeandPattern: latestRecord.treadMakeandPattern
    };
  };

  // Fetch and process data
  const handleSearch = async () => {
    const searchSerial = tyreSerialNumber.trim();
    
    if (!searchSerial) {
      toast.error("Please enter a tyre serial number!");
      return;
    }

    setLoading(true);
    setTyreData(null);
    
    try {
      const allRecords = [];
      let hasMore = true;
      let offset = 0;
      const limit = 500;

      while (hasMore) {
        const queries = [
          Query.equal("tyreSerialNumber", searchSerial),
          Query.limit(limit),
          Query.offset(offset)
        ];

        const response = await databases.listDocuments(
          'AppwriteCloudFEApp',
          'FitmentData',
          queries
        );

        allRecords.push(...response.documents);
        
        if (response.documents.length < limit) {
          hasMore = false;
        } else {
          offset += limit;
        }
      }

      if (allRecords.length === 0) {
        toast.error("No records found for this tyre serial number!");
        setLoading(false);
        return;
      }

      const processed = processSingleTyre(allRecords);
      setTyreData(processed);
    } catch (error) {
      toast.error("Error fetching data!");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const exportToExcel = () => {
    if (!tyreData) return;

    const exportData = tyreData.records.map(record => ({
      'Tyre Serial Number': record.tyreSerialNumber,
      'Fleet Name': record.fleetName,
      'Vehicle Registration': record.vehicleRegistrationNumber,
      'Vehicle Type': record.vehicleType,
      'Position': record.position,
      'Tyre Size': record.tyreSize,
      'Tyre Make & Pattern': record.tyreMakeandPattern,
      'Tread Make & Pattern': record.treadMakeandPattern,
      'Tyre Status': record.tyreStatus,
      'Fitment Date': record.fitmentDate,
      'Fitment ODO': record.ftmentODO,
      'Removal Date': record.removalDate || '-',
      'Removal ODO': record.removalODO || '-',
      'Run (km)': record.run !== null ? record.run.toFixed(2) : '-',
      'TD Diff': record.tdDiff !== null ? record.tdDiff.toFixed(2) : '-'
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = { Sheets: { 'Tyre History': ws }, SheetNames: ['Tyre History'] };
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    FileSaver.saveAs(data, `Tyre_${tyreData.tyreSerialNumber}_History.xlsx`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      
      {/* Hero Search Section */}
      <div className='max-w-5xl mx-auto pt-12 pb-8 px-6'>
        <div className='text-center mb-8'>
          <h1 className='text-3xl md:text-4xl font-bold text-slate-800 mb-3'>
            Tyre Lifecycle 
          </h1>
         
        </div>

        <div className='bg-white rounded-2xl shadow-xl border border-slate-200 p-8'>
          <div className='flex flex-col md:flex-row gap-4'>
            <div className='flex-1 relative'>
              <Search className='absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5' />
              <input
                type='text'
                className='w-full pl-12 pr-4 py-4 rounded-xl text-base border-2 border-slate-200 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-100 transition-all'
                placeholder='Enter Tyre Serial Number'
                value={tyreSerialNumber}
                onChange={(e) => setTyreSerialNumber(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={loading}
              />
            </div>
            <button 
              onClick={handleSearch}
              disabled={loading}
              className='bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold px-10 py-4 rounded-xl hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
            >
              {loading ? (
                <span className='flex items-center gap-2'>
                  <div className='w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin'></div>
                  Searching...
                </span>
              ) : (
                'Search'
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Results Section */}
      {tyreData && (
        <div className='max-w-7xl mx-auto px-6 pb-12'>
          {/* Header Card with Export */}
          <div className='bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden mb-6'>
            <div className='bg-gradient-to-r from-slate-800 to-slate-900 px-8 py-6'>
              <div className='flex flex-col md:flex-row justify-between items-start md:items-center gap-4'>
                <div>
                  <div className='flex items-center gap-3 mb-2'>
                    <Package className='w-7 h-7 text-blue-400' />
                    <h2 className='text-2xl font-bold text-white'>
                      {tyreData.tyreSerialNumber}
                    </h2>
                  </div>
                  <div className='flex flex-wrap gap-2 mt-3'>
                    <span className={`px-4 py-1.5 rounded-full text-sm font-semibold ${
                      tyreData.latestStatus.includes('Running') 
                        ? 'bg-emerald-500 text-white' 
                        : 'bg-blue-500 text-white'
                    }`}>
                      {tyreData.latestStatus}
                    </span>
                    <span className='px-4 py-1.5 rounded-full text-sm font-semibold bg-white/20 text-white backdrop-blur'>
                      {tyreData.recordCount} Total Fitments
                    </span>
                    <span className='px-4 py-1.5 rounded-full text-sm font-semibold bg-white/20 text-white backdrop-blur'>
                      {tyreData.completedFitments} Completed
                    </span>
                  </div>
                </div>
                <button 
                  onClick={exportToExcel}
                  className='bg-emerald-500 text-white px-6 py-3 rounded-xl hover:bg-emerald-600 transition-all shadow-lg font-semibold flex items-center gap-2 group'
                >
                  <Download className='w-5 h-5 group-hover:animate-bounce' />
                  Export Excel
                </button>
              </div>
            </div>

            {/* Key Metrics Grid */}
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 p-8'>
              <div className='relative overflow-hidden bg-gradient-to-br from-blue-500 to-blue-600 p-6 rounded-xl shadow-lg group hover:shadow-xl transition-all'>
                <div className='relative z-10'>
                  <div className='flex items-center gap-2 text-blue-100 font-semibold mb-2'>
                    <MapPin className='w-4 h-4' />
                    Fleet Name
                  </div>
                  <div className='text-2xl font-bold text-white truncate'>{tyreData.fleetName}</div>
                </div>
                <div className='absolute -right-4 -bottom-4 w-24 h-24 bg-white/10 rounded-full group-hover:scale-110 transition-transform'></div>
              </div>
              
              <div className='relative overflow-hidden bg-gradient-to-br from-purple-500 to-purple-600 p-6 rounded-xl shadow-lg group hover:shadow-xl transition-all'>
                <div className='relative z-10'>
                  <div className='flex items-center gap-2 text-purple-100 font-semibold mb-2'>
                    <Activity className='w-4 h-4' />
                    Vehicle Type
                  </div>
                  <div className='text-2xl font-bold text-white truncate'>{tyreData.vehicleType}</div>
                </div>
                <div className='absolute -right-4 -bottom-4 w-24 h-24 bg-white/10 rounded-full group-hover:scale-110 transition-transform'></div>
              </div>
              
              <div className='relative overflow-hidden bg-gradient-to-br from-emerald-500 to-emerald-600 p-6 rounded-xl shadow-lg group hover:shadow-xl transition-all'>
                <div className='relative z-10'>
                  <div className='flex items-center gap-2 text-emerald-100 font-semibold mb-2'>
                    <TrendingUp className='w-4 h-4' />
                    Total Run
                  </div>
                  <div className='text-2xl font-bold text-white'>{tyreData.totalRun.toFixed(0)} km</div>
                </div>
                <div className='absolute -right-4 -bottom-4 w-24 h-24 bg-white/10 rounded-full group-hover:scale-110 transition-transform'></div>
              </div>
              
              <div className='relative overflow-hidden bg-gradient-to-br from-orange-500 to-orange-600 p-6 rounded-xl shadow-lg group hover:shadow-xl transition-all'>
                <div className='relative z-10'>
                  <div className='flex items-center gap-2 text-orange-100 font-semibold mb-2'>
                    <Activity className='w-4 h-4' />
                    Total TD Diff
                  </div>
                  <div className='text-2xl font-bold text-white'>{tyreData.totalTDDiff.toFixed(0)} mm</div>
                </div>
                <div className='absolute -right-4 -bottom-4 w-24 h-24 bg-white/10 rounded-full group-hover:scale-110 transition-transform'></div>
              </div>
            </div>

            {/* Additional Details */}
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4 px-8 pb-8'>
              <div className='bg-slate-50 p-5 rounded-xl border border-slate-200'>
                <div className='text-sm text-slate-600 font-semibold mb-1'>Tyre Size</div>
                <div className='text-lg font-bold text-slate-900'>{tyreData.tyreSize}</div>
              </div>
              
              <div className='bg-slate-50 p-5 rounded-xl border border-slate-200'>
                <div className='text-sm text-slate-600 font-semibold mb-1'>Tyre Make & Pattern</div>
                <div className='text-lg font-bold text-slate-900'>{tyreData.tyreMakeandPattern}</div>
              </div>
              
              {tyreData.treadMakeandPattern && (
                <div className='bg-slate-50 p-5 rounded-xl border border-slate-200 md:col-span-2'>
                  <div className='text-sm text-slate-600 font-semibold mb-1'>Tread Make & Pattern</div>
                  <div className='text-lg font-bold text-slate-900'>{tyreData.treadMakeandPattern}</div>
                </div>
              )}
            </div>
          </div>

          {/* Fitment History Timeline */}
          <div className='mb-6'>
                          <div className='flex items-center gap-3 mb-6'>
              <Calendar className='w-5 h-5 text-slate-700' />
              <h3 className='text-xl font-bold text-slate-800'>Fitment History Timeline</h3>
            </div>
          </div>
          
          <div className='space-y-4'>
            {tyreData.records.map((record, index) => (
              <div 
                key={index} 
                className='bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden hover:shadow-xl transition-all group'
              >
                <div className={`h-2 ${
                  record.fitmentStatus === "Removed"
                    ? 'bg-gradient-to-r from-emerald-400 to-emerald-500' 
                    : 'bg-gradient-to-r from-amber-400 to-amber-500'
                }`}></div>
                
                <div className='p-6'>
                  <div className='flex flex-col md:flex-row justify-between items-start gap-4 mb-5'>
                    <div className='flex-1'>
                      <div className='flex items-center gap-3 mb-2'>
                        <h4 className='text-lg font-bold text-slate-800'>
                          {record.vehicleRegistrationNumber}
                        </h4>
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          record.tyreStatus === 'New Tyre' ? 'bg-blue-100 text-blue-700 border border-blue-200' :
                          record.tyreStatus === 'New Running Tyre' ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' :
                          record.tyreStatus === 'Retread Tyre' ? 'bg-purple-100 text-purple-700 border border-purple-200' :
                          'bg-orange-100 text-orange-700 border border-orange-200'
                        }`}>
                          {record.tyreStatus}
                        </span>
                      </div>
                      <p className='text-slate-600 flex items-center gap-2'>
                        <MapPin className='w-4 h-4' />
                        Position: <span className='font-semibold'>{record.position}</span>
                      </p>
                    </div>
                    {!record.fitmentStatus == "Removed" && (
                      <span className='px-4 py-2 rounded-full text-sm font-bold bg-amber-100 text-amber-700 border-2 border-amber-300 flex items-center gap-2'>
                        <div className='w-2 h-2 bg-amber-500 rounded-full animate-pulse'></div>
                        Active
                      </span>
                    )}
                  </div>

                  {/* Fitment Details */}
                  <div className='bg-slate-50 rounded-lg p-5 mb-4'>
                    <div className='text-xs font-bold text-slate-500 uppercase mb-3 tracking-wider'>Fitment Details</div>
                    <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
                      <div>
                        <div className='text-xs text-slate-500 font-semibold mb-1'>Date</div>
                        <div className='text-sm font-bold text-slate-800'>{record.fitmentDate}</div>
                      </div>
                      <div>
                        <div className='text-xs text-slate-500 font-semibold mb-1'>Odometer</div>
                        <div className='text-sm font-bold text-slate-800'>{record.ftmentODO} km</div>
                      </div>
                      <div>
                        <div className='text-xs text-slate-500 font-semibold mb-1'>Tread Depth</div>
                        <div className='text-sm font-bold text-slate-800'>{record.TD || '-'} mm</div>
                      </div>
                      <div>
                        <div className='text-xs text-slate-500 font-semibold mb-1'>Remarks</div>
                        <div className='text-sm font-bold text-slate-800 truncate'>{record.remarks || '-'}</div>
                      </div>
                    </div>
                  </div>

                  {/* Removal Details */}
                  {record.hasRemovalODO && (
                    <div className='bg-emerald-50 rounded-lg p-5 border-2 border-emerald-200'>
                      <div className='text-xs font-bold text-emerald-700 uppercase mb-3 tracking-wider'>Removal Details</div>
                      <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
                        <div>
                          <div className='text-xs text-emerald-600 font-semibold mb-1'>Date</div>
                          <div className='text-sm font-bold text-slate-800'>{record.removalDate || '-'}</div>
                        </div>
                        <div>
                          <div className='text-xs text-emerald-600 font-semibold mb-1'>Odometer</div>
                          <div className='text-sm font-bold text-slate-800'>{record.removalODO} km</div>
                        </div>
                        <div>
                          <div className='text-xs text-emerald-600 font-semibold mb-1'>Tread Depth</div>
                          <div className='text-sm font-bold text-slate-800'>{record.removalTD || '-'} mm</div>
                        </div>
                        <div className='bg-gradient-to-br from-emerald-500 to-emerald-600 p-3 rounded-lg text-white'>
                          <div className='text-xs font-bold mb-1 opacity-90'>Total Run</div>
                          <div className='text-lg font-bold'>{record.run.toFixed(0)} km</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && !tyreData && (
        <div className='text-center py-20 px-6'>
          <div className='inline-block p-6 bg-white rounded-full shadow-lg mb-6'>
            <Search className='w-16 h-16 text-slate-400' />
          </div>
          <h3 className='text-2xl font-bold text-slate-700 mb-2'>
            Ready to Track
          </h3>
          <p className='text-lg text-slate-500 max-w-md mx-auto'>
            Enter a tyre serial number above to view its complete lifecycle history and performance metrics
          </p>
        </div>
      )}
    </div>
  );
};

export default TyreLifecycleAnalysis;